import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, Star } from 'lucide-react';
import SectionHead from '@/components/SectionHead';
import { PACKAGES } from '@/data/content';
import { fadeUp, stagger, viewportOnce } from '@/lib/motion';

const registerLink = (name) => `/register?program=${encodeURIComponent(name)}`;

export default function Pricing() {
  return (
    <section id="programs" className="relative py-24 sm:py-32 bg-night-surface border-y border-white/5 overflow-hidden" data-testid="pricing-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.07),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <SectionHead
          chapter="04"
          eyebrow="Official Academy Packages"
          title={'CHOOSE YOUR\nDEVELOPMENT PATH.'}
          description="Official monthly training packages. Professional coaching, small groups, and a clear pathway for every player."
          testId="pricing-heading"
        />

        <motion.div
          variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}
          className="grid lg:grid-cols-3 gap-6 items-stretch"
        >
          {PACKAGES.map((p, i) => (
            <motion.div
              key={p.id} variants={fadeUp} custom={i}
              whileHover={{ y: -8 }}
              className={`relative flex flex-col rounded-sm p-8 transition-[transform,box-shadow,border-color] duration-300 ${
                p.elite
                  ? 'border border-brand-gold/70 bg-gradient-to-b from-brand-gold/15 via-night to-night shadow-[0_0_60px_rgba(197,160,89,0.18)]'
                  : p.badge
                    ? 'border border-brand-red/60 bg-night shadow-[0_0_50px_rgba(230,57,70,0.14)]'
                    : 'border border-white/10 bg-night'
              }`}
              data-testid={`package-${p.id}`}
            >
              {p.badge && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-brand-red text-white text-[11px] font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full flex items-center gap-1.5" data-testid="package-badge-most-popular">
                  <Star size={11} fill="currentColor" /> {p.badge}
                </span>
              )}
              {p.elite && (
                <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-goldlight to-brand-gold text-night text-[11px] font-bold tracking-[0.25em] uppercase px-4 py-1.5 rounded-full" data-testid="package-badge-elite">
                  Premium
                </span>
              )}
              <h3 className={`font-display text-3xl tracking-widest uppercase ${p.elite ? 'text-transparent bg-clip-text bg-gradient-to-r from-brand-goldlight to-brand-gold' : 'text-white'}`}>
                {p.name}
              </h3>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-6xl sm:text-7xl leading-none text-white">${p.price}</span>
                <span className="text-gray-500 text-sm uppercase tracking-widest mb-1.5">/ {p.per || 'Month'}</span>
              </div>
              <div className={`mt-2 text-xs font-bold uppercase tracking-[0.25em] ${p.elite ? 'text-brand-gold' : 'text-brand-red'}`}>{p.cadence}</div>
              <p className="mt-4 text-sm text-gray-400 leading-relaxed">{p.blurb}</p>
              <ul className="mt-6 space-y-2.5 flex-1">
                {p.features.map(f => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-gray-300">
                    <Check size={16} className={`mt-0.5 shrink-0 ${p.elite ? 'text-brand-gold' : 'text-brand-red'}`} />
                    {f}
                  </li>
                ))}
              </ul>
              <Link
                to={registerLink(p.name)}
                data-testid={`package-cta-${p.id}`}
                className={`mt-8 text-center font-display tracking-widest text-xl py-3.5 rounded-sm transition-colors duration-300 ${
                  p.elite
                    ? 'bg-gradient-to-r from-brand-goldlight to-brand-gold text-night hover:brightness-110'
                    : 'bg-brand-red text-white hover:bg-brand-redhover'
                }`}
              >
                {p.cta.toUpperCase()}
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
